import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { withReturnTo } from '../common/returnPath';
import api from '../../services/api';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import type {
  ManagedSiteGitHubRepo,
  GitHubRepo,
  GitHubRepoRole,
  LinkRepoToSiteRequest,
} from '../../types';

const ROLE_OPTIONS: { value: GitHubRepoRole; label: string; description: string }[] = [
  { value: 'PRIMARY', label: 'Primary', description: 'Main repository containing all site content' },
  { value: 'CONFIG', label: 'Config', description: 'Hugo/Gatsby configuration files' },
  { value: 'CONTENT', label: 'Content', description: 'Content files (markdown, etc.)' },
  { value: 'THEME', label: 'Theme', description: 'Theme/template files' },
];

interface Props {
  siteId: number;
  siteType: string;
}

export default function SiteGitHubRepos({ siteId, siteType }: Props) {
  const here = useLocation();
  const [linkedRepos, setLinkedRepos] = useState<ManagedSiteGitHubRepo[]>([]);
  const [availableRepos, setAvailableRepos] = useState<GitHubRepo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkForm, setLinkForm] = useState<LinkRepoToSiteRequest>({
    github_repo_id: 0,
    role: 'PRIMARY',
    auto_sync: true,
  });
  const [linking, setLinking] = useState(false);

  useEscapeKey(showLinkModal, () => setShowLinkModal(false));

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [linked, allRepos] = await Promise.all([
        api.getSiteGitHubRepos(siteId),
        api.getGitHubRepos(),
      ]);
      setLinkedRepos(linked);
      setAvailableRepos(allRepos);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load repositories');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLinkRepo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkForm.github_repo_id) {
      setError('Please select a repository');
      return;
    }
    setLinking(true);
    try {
      const newLink = await api.linkRepoToSite(siteId, linkForm);
      setLinkedRepos([...linkedRepos, newLink]);
      setShowLinkModal(false);
      setLinkForm({ github_repo_id: 0, role: 'PRIMARY', auto_sync: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link repository');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkRepo = async (linkId: number) => {
    if (!window.confirm('Are you sure you want to unlink this repository?')) {
      return;
    }
    try {
      await api.unlinkRepoFromSite(siteId, linkId);
      setLinkedRepos(linkedRepos.filter(r => r.id !== linkId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink repository');
    }
  };

  // Filter out already linked repos
  const linkedRepoIds = new Set(linkedRepos.map(r => r.github_repo_id));
  const unlinkedRepos = availableRepos.filter(r => !linkedRepoIds.has(r.id));

  // Only Hugo sites are backed by a repository we commit into.
  if (siteType !== 'HUGO') {
    return null;
  }

  if (loading) {
    return (
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <p className="text-gray-500 dark:text-gray-400">Loading GitHub repositories...</p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">GitHub Repositories</h3>
        {availableRepos.length > 0 ? (
          <button
            onClick={() => setShowLinkModal(true)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            + Link Repository
          </button>
        ) : (
          <Link
            to={withReturnTo('/settings?tab=connections&sub=github', `${here.pathname}${here.search}`, 'Deliver')}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            Connect GitHub
          </Link>
        )}
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 p-2 rounded">
          {error}
        </div>
      )}

      {linkedRepos.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No repositories linked yet.
            {availableRepos.length === 0 ? (
              <>
                <br />
                <Link to={withReturnTo('/settings?tab=connections&sub=github', `${here.pathname}${here.search}`, 'Deliver')} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                  Connect your GitHub account
                </Link>
                {' '}to link repositories.
              </>
            ) : (
              <>
                {' '}
                <button
                  onClick={() => setShowLinkModal(true)}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  Link a repository
                </button>
              </>
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {linkedRepos.map((link) => (
            <div
              key={link.id}
              className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg p-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {link.repo?.full_name || 'Unknown repo'}
                  </span>
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                    {link.role}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Branch: {link.branch || 'default'}
                  {link.path_prefix && <> &bull; Path: {link.path_prefix}</>}
                </p>
              </div>
              <button
                onClick={() => handleUnlinkRepo(link.id)}
                className="text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 ml-2"
                title="Unlink repository"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Link Repository Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Link GitHub Repository</h3>
            </div>
            <form onSubmit={handleLinkRepo} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Repository
                </label>
                <select
                  value={linkForm.github_repo_id}
                  onChange={(e) => setLinkForm({ ...linkForm, github_repo_id: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2"
                  required
                >
                  <option value={0}>Select a repository...</option>
                  {unlinkedRepos.map((repo) => (
                    <option key={repo.id} value={repo.id}>
                      {repo.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={linkForm.role}
                  onChange={(e) => setLinkForm({ ...linkForm, role: e.target.value as GitHubRepoRole })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2"
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} - {opt.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Branch (optional)
                </label>
                <input
                  type="text"
                  value={linkForm.branch || ''}
                  onChange={(e) => setLinkForm({ ...linkForm, branch: e.target.value || undefined })}
                  placeholder="Leave empty for default branch"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Path Prefix (optional)
                </label>
                <input
                  type="text"
                  value={linkForm.path_prefix || ''}
                  onChange={(e) => setLinkForm({ ...linkForm, path_prefix: e.target.value || undefined })}
                  placeholder="e.g., content/posts"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="auto_sync"
                  checked={linkForm.auto_sync}
                  onChange={(e) => setLinkForm({ ...linkForm, auto_sync: e.target.checked })}
                  className="h-4 w-4 text-blue-600 dark:text-blue-400 rounded border-gray-300 dark:border-gray-600"
                />
                <label htmlFor="auto_sync" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Auto-sync on changes
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowLinkModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={linking || !linkForm.github_repo_id}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {linking ? 'Linking...' : 'Link Repository'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
